import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation, HostListener } from '@angular/core';
import { Observable, concat, of, from, Subscription } from 'rxjs';
import { map, concatMap, switchMap, debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { CliListEntry } from 'src/app/shared/cli-list-entry';
import { Person } from 'src/app/shared/person';
import { Client } from 'src/app/shared/client';
import { DocItems, Document } from 'src/app/shared/document';
import { Product } from 'src/app/shared/product';
import { round2 } from 'src/app/shared/round';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { DocumentApiService } from 'src/app/services/document-api.service';
import { DocItemPricingService } from 'src/app/services/doc-item-pricing.service';
import { ClientApiService } from 'src/app/services/client-api.service';

@Component({
  selector: 'app-new-document-dlg-doc-items',
  templateUrl: './new-document-dlg-doc-items.component.html',
  styleUrls: ['./new-document-dlg-doc-items.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class NewDocumentDlgDocItemsComponent implements OnInit, OnChanges, OnDestroy {

  private globalSettinsData: GlobalSettingsData;
  private _productInfoQuery: Subscription;

  @Input() newDocument: Document;

  @Input() showStock: boolean = true;
  @Input() showShipped: boolean = true;
  @Input() showReserve: boolean = true;
  @Input() showSold: boolean = true;
  @Input() showNote: boolean = true;
  @Input() showNote2: boolean = false;
  @Input() showDiscount: boolean = true;
  @Input() showPrice: boolean = true;
  @Input() showVprice: boolean = true;
  @Input() showDepartments: boolean = true;

  @Input() showU4etka: boolean = false;

  @Input() U4ETKALabel: string = 'Учетка';
  @Input() docNote1Header: string = 'Комментарий';
  @Input() docNote2Header: string = 'Примечание';

  /**
   * Turns this table from read-only into a capture panel: a product picker above it, and
   * editable quantity/price cells. Defaults to false so every existing caller -- the
   * parent-derived, payment, spec and formular tables, whose rows are generated rather
   * than chosen -- keeps exactly the behaviour it has today.
   */
  @Input() allowAdd: boolean = false;

  selection: DocItems[] = [];

  /** Product chosen in the picker but not yet added -- cleared on add. */
  pendingProduct: Product = null;
  pendingQty: number = 1;
  /** Set while the price lookup for a just-added row is in flight. */
  addingPrice: boolean = false;

  public get showDocumentDetails(): boolean { return (this.newDocument && [4, 5, 7].includes(this.newDocument.DOCTYPE)); }

  /** Row count before the "Цена $" column, so the footer's "Итого:" label can span exactly
   *  the columns actually shown - the set of visible columns varies per caller (allowAdd,
   *  showNote2, showU4etka, etc.). */
  public get footerLeadColspan(): number {
    return 2 // Код товара, Название товара
      + (this.showNote ? 1 : 0)
      + (this.showNote2 ? 1 : 0)
      + 1 // Количество
      + (this.showShipped ? 1 : 0)
      + (this.showStock ? 1 : 0)
      + (this.showReserve ? 1 : 0)
      + (this.showSold ? 1 : 0)
      + (this.showDiscount ? 1 : 0)
      + (this.showU4etka ? 1 : 0);
  }

  /** Total in value/main currency ("Цена $" column) - DOCITEMVPRICE is a unit price, so the
   *  total has to be re-derived per row rather than summed directly. */
  public get totalValueSum(): number {
    return (this.newDocument?.items ?? []).reduce((s, i) => s + ((i.DOCITEMVPRICE ?? 0) * (i.DOCITEMQTY ?? 0)), 0);
  }

  /** Total in document currency ("Сумма" column) - DOCITEMSUM is already a row total. */
  public get totalDocumentSum(): number {
    return (this.newDocument?.items ?? []).reduce((s, i) => s + (i.DOCITEMSUM ?? 0), 0);
  }

  constructor(
    private pricing: DocItemPricingService,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  public get canAdd(): boolean {
    return this.allowAdd && !!this.pendingProduct && (this.pendingQty > 0);
  }

  onPendingProductSelected(product: Product): void {
    this.pendingProduct = product;
  }

  /**
   * Appends the picked product as a new row, then fills its price from the catalogue.
   *
   * Pricing is deliberately the minimal version (owner's call): unit price and VAT from
   * `getProdInfoById`, using the same formula `edit-document-content` applies at zero
   * discount. The discount tiers, FIFO income price, profit and row-highlight flags that
   * component also computes are analysis of a *saved* document -- they need stock and
   * history this document does not have yet, and the edit page recomputes all of them on
   * open anyway. Capturing what to sell is this panel's job.
   *
   * The row is appended before the lookup returns, so a slow or failing price call leaves
   * a usable row at price 0 rather than swallowing the product -- and both cells are
   * editable, so any wrong figure is correctable in place.
   */
  onAddProduct(): void {
    if (!this.canAdd || !this.newDocument) { return; }

    const doc = this.newDocument;
    const product = this.pendingProduct;

    // Negative, descending, like every other pre-save row in this dialog -- real
    // DOCITEMIDs are assigned by the API on POST.
    const nextTempId = -2 - (doc.items?.length ?? 0);

    const row: DocItems = {
      DOCITEMID: nextTempId,
      DOCID: null,
      DOCTYPE: doc.DOCTYPE,
      PRODID: product.PRODID,
      PRODCODE: product.PRODCODE,
      DOCITEMPRODNAME: product.PRODNAME,
      DOCITEMPRICE: 0,
      DOCITEMQTY: this.pendingQty,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: doc.DOCCURRID,
      DOCITEMCRATE: doc.DOCRATEVAL || 1,
      PAYITEMCLIID: null,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: '',
      CURCODE: '',
      U4ETKA: null,
    };

    doc.items = [...(doc.items ?? []), row];
    this.pendingProduct = null;
    this.pendingQty = 1;

    this.fillRowPrice(row);
  }

  /** Delegates to DocItemPricingService so a hand-picked product and an AI-proposed one
   *  are priced by exactly the same call -- they used to differ, and an identical product
   *  came back priced or at 0 depending only on how it entered the document. */
  private fillRowPrice(row: DocItems): void {
    if (!this.newDocument) { return; }

    this.addingPrice = true;
    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }

    this._productInfoQuery = this.pricing.priceRow(row, this.newDocument).subscribe({
      next: () => { this.addingPrice = false; },
      error: () => { this.addingPrice = false; },
    });
  }

  /** Hand edits to quantity or price go through the same derivation the pricing lookup
   *  and the edit page both use, so "Цена $", the discount and both totals stay in step
   *  with the figure actually typed. */
  recalcRowSum(row: DocItems): void {
    if (!this.newDocument) { return; }
    this.pricing.recalcRowFromPrice(row, this.newDocument);
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    return true;
  }

updateProdInfo() {
    console.log('*** NewDocumentDlgDocItemsComponent updateProdInfo ');
  }

  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** NewDocumentDlgDocItemsComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      //console.log('*** NewDocumentDlgDocItemsComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      switch (propName) {

        case 'newDocument':
        case 'docItems':
//          this.updateProdInfo();
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** NewDocumentDlgDocItemsComponent in ngOnDestroy')

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    console.log('*** NewDocumentDlgDocItemsComponent in ngOnInit');

    // Declared since this component was written but never populated; the VAT fallback in
    // fillRowPrice() is the first thing here to need it.
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();
  }

  onUnSelectAll(event) {
    console.log('*** NewDocumentDlgDocItemsComponent in onUnSelectAll: ' + JSON.stringify(event));

    this.selection = [];
  }

  onSelectAll(event) {
    console.log('*** NewDocumentDlgDocItemsComponent in onSelectAll: ' + JSON.stringify(event));

    this.selection = this.newDocument.items;
  }

  onRemoveRow(event) {
    console.log('*** NewDocumentDlgDocItemsComponent in onRemoveRow: ' + JSON.stringify(event));

    if (this.selection) {
      [... this.selection].forEach(row => {
        let i = this.newDocument.items.findIndex(e => e.PRODID == row.PRODID);

        console.log('*** NewDocumentDlgDocItemsComponent in onRemoveRow: i=' + i);

        if (i >= 0) {
          this.newDocument.items.splice(i, 1);
        }

      });

      this.selection = [];
    }
  }
}
